/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react';
import { useSelect } from 'downshift';
import produce, { type Draft } from 'immer';
import {
  type CustomButton,
  CustomButtonType,
  FeatureType,
  PanelType,
} from '@types';
import { Collapse } from '@components/Collapse';
import Button from '@components/Button';
import Tip from '@components/Tip';
import SortableList from '@components/SortableList';
import useToggle from '@hooks/useToggle';
import arrayMove from '@utils/arrayMove';
import { $panelApi } from '@stores/panelStores';
import useTemplateFeature from '@hooks/useTemplateFeature';
import { customButtonDefault } from '@modules/edit';
import VipWrapper from '@components/VipWrapper';
import showVipPrompt from '@components/ConfirmModal/showVipPrompt';
import CustomButtonItem from './CustomButtonItem';
import { CustomButtonContext } from './context';

const SUB_MAX = 5;
const G = '创建分组';
const B = '创建按钮';
const items = [B, G];

interface Props {
  hideVIP?: boolean;
  value: CustomButton[];
  onChange: (val: CustomButton[]) => void;
}

function CustomButtonSetting({ hideVIP, value, onChange }: Props) {
  const sorting = useToggle(false);
  const cButtonNum = useTemplateFeature('customButton');

  const handleChange = (
    fn: (draft: Draft<CustomButton[]>) => void | CustomButton[]
  ) => {
    onChange(produce(value, (draft) => fn(draft)));
  };

  const createNewGroup = () => {
    const groupIndex = value.length;
    handleChange((draft) => {
      draft[groupIndex] = {
        type: CustomButtonType.group,
        iconMediaId: 0,
        iconMediaUrl: '',
        groupTitle: '',
        children: [],
      };
    });
    $panelApi.show({
      type: PanelType.customGroup,
      groupIndex,
      isNew: true,
    });
  };

  const createNewItem = () => {
    const groupIndex = value.length;
    handleChange((draft) => {
      draft[groupIndex] = {
        type: CustomButtonType.button,
        iconMediaId: 0,
        iconMediaUrl: '',
        groupTitle: '',
        children: [customButtonDefault(FeatureType.link)],
      };
    });
    $panelApi.show({
      type: PanelType.customButton,
      groupIndex,
      buttonIndex: 0,
      isNew: true,
    });
  };

  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps } =
    useSelect({
      items,
      selectedItem: null,
      onSelectedItemChange: (changes) => {
        if (changes.selectedItem === G) {
          createNewGroup();
        } else if (changes.selectedItem === B) {
          createNewItem();
        }
      },
    });

  const contextValue = useMemo(
    () => ({ onChange: handleChange }),
    [handleChange]
  );

  return (
    <CustomButtonContext.Provider value={contextValue}>
      <Collapse
        title={
          <div className="flex items-center">
            <VipWrapper showVip={!hideVIP}>自定义按钮设置</VipWrapper>
            <div className="ml-5 text-black-gray">
              {`${value.length}/${cButtonNum}`}
            </div>
          </div>
        }
      >
        <div className="relative">
          <Button
            className="w-full"
            disabled={value.length >= cButtonNum}
            {...getToggleButtonProps()}
            onClick={(event) => {
              getToggleButtonProps().onClick(event);
              showVipPrompt();
            }}
          >
            创建
          </Button>
          <div
            className="absolute z-30 mt-5 w-full select-none overflow-hidden rounded bg-white text-black focus:outline-none"
            {...getMenuProps()}
          >
            {isOpen &&
              items.map((item, index) => (
                <div
                  className="flex h-30 cursor-pointer items-center justify-center px-10 hover:bg-white-light"
                  key={item}
                  {...getItemProps({ item, index })}
                >
                  {item}
                </div>
              ))}
          </div>
        </div>
        {value.length === 0 && (
          <>
            <Tip className="mt-10">{`注：可添加${cButtonNum}个按钮或分组, 每个分组最多可添加${SUB_MAX}个子按钮`}</Tip>
            <Tip>添加后支持上下拖拽调整按钮/子按钮的先后顺序。</Tip>
          </>
        )}
        <SortableList
          axis="y"
          lockAxis="y"
          distance={10}
          updateBeforeSortStart={() => {
            sorting.on();
          }}
          onSortEnd={(sort) => {
            handleChange((draft) => {
              return arrayMove({
                array: draft,
                fromIndex: sort.oldIndex,
                toIndex: sort.newIndex,
              });
            });
            sorting.off();
          }}
        >
          <div>
            {value.map((x, i) => (
              <CustomButtonItem
                key={i}
                data={x}
                index={i}
                sorting={sorting.isOn}
                groupIndex={i}
                subMax={SUB_MAX}
              />
            ))}
          </div>
        </SortableList>
      </Collapse>
    </CustomButtonContext.Provider>
  );
}

export default CustomButtonSetting;
